/* Trilho de 340px ao lado do formulário: verificações, resumo e as ações.
   Emitir só libera depois de Validar, e qualquer edição volta a exigir validação. */
import React from 'react';
import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { CheckCircle, PaperPlaneTilt, Warning } from 'phosphor-react';
import { useWatch } from 'react-hook-form';
import {
  INfseEmitente,
  INfseMensagemSefin,
  INfseValidacao,
  NfseVerificacaoCodigo,
} from '../../../../../../types/nfseEmissao';
import { toNumber } from '../../../../../../utils/FormatFunctions';
import {
  Banner,
  brl,
  C,
  Card,
  CardHead,
  CheckItem,
  CheckList,
  dataBR,
  MONO,
  NButton,
} from '../../shared/ui';

interface VerificacoesPanelProps {
  emitente?: INfseEmitente;
  validacao?: INfseValidacao | null;
  errosSefin?: INfseMensagemSefin[] | null;
  numeroDps?: number | null;
  serie?: string;
  bloqueado: boolean;
  reenvio: boolean;
  salvando: boolean;
  validando: boolean;
  enviando: boolean;
  onSalvar: () => void;
  onValidar: () => void;
  onEmitir: () => void;
  onUsarProximoLivre: (numero: number) => void;
}

function verificacoes(
  emitente: INfseEmitente | undefined,
  validacao: INfseValidacao | null | undefined,
  validando: boolean,
  numero?: number | null,
): CheckItem[] {
  const resultado = (regra: NfseVerificacaoCodigo) =>
    validacao?.verificacoes.find(v => v.regra === regra);
  const doServidor = (regra: NfseVerificacaoCodigo, item: CheckItem): CheckItem => {
    const v = resultado(regra);
    if (validando && (regra === 'RN16' || regra === 'XSD')) return { ...item, state: 'running' };
    if (!v) return item;
    return { ...item, state: v.ok ? 'ok' : 'fail', detail: v.ok ? item.detail : v.mensagem };
  };

  let mun: CheckItem = { title: 'Município', state: 'pending', detail: 'escolha a empresa' };
  let cert: CheckItem = { title: 'Certificado A1', state: 'pending', detail: 'escolha a empresa' };
  if (emitente) {
    const semMunicipio = ['sem_ibge', 'municipio_nao_aderente'].includes(emitente.situacao);
    mun = {
      title: 'Município',
      state: semMunicipio ? 'fail' : 'ok',
      detail: emitente.codigo_ibge
        ? `${emitente.municipio}/${emitente.uf} bate com o IBGE ${emitente.codigo_ibge}${
            emitente.situacao === 'municipio_nao_aderente' ? ' · não aderente' : ' e está aderente'
          }`
        : 'sem código IBGE no cadastro',
    };
    const cs = emitente.certificado_situacao;
    let certDetalhe = 'não enviado';
    if (cs === 'vencido') certDetalhe = `venceu em ${dataBR(emitente.certificado_valido_ate)}`;
    if (cs === 'valido' || cs === 'a_vencer')
      certDetalhe = `válido até ${dataBR(emitente.certificado_valido_ate)}`;
    if (cs === 'a_vencer') certDetalhe += ` · ${emitente.certificado_dias} dias`;
    cert = {
      title: 'Certificado A1',
      state: { valido: 'ok', a_vencer: 'warn', vencido: 'fail', ausente: 'fail' }[
        cs
      ] as CheckItem['state'],
      detail: certDetalhe,
    };
  }
  return [
    doServidor('RN14', mun),
    doServidor('RN15', cert),
    doServidor('RN16', {
      title: numero ? `Número ${numero}` : 'Número da DPS',
      state: 'pending',
      detail: resultado('RN16')?.ok
        ? 'livre na tb_nfse, nas emissões e na Sefin'
        : 'confere ao validar (RN16)',
    }),
    doServidor('XSD', {
      title: 'Leiaute DPS v1.01',
      state: 'pending',
      detail: resultado('XSD')?.ok ? 'XML válido no XSD' : 'confere ao validar',
    }),
  ];
}

function Resumo() {
  const [vServ, pAliq, trib, tpRet, retencoes] = useWatch({
    name: ['v_serv', 'p_aliq', 'trib_issqn', 'tp_ret_issqn', 'retencoes'],
  });
  const valor = toNumber(vServ);
  const aliquota = toNumber(pAliq);
  const iss = String(trib) === '1' ? (valor * aliquota) / 100 : 0;
  const issRetido = String(tpRet) !== '1' ? iss : 0;
  const federais = Object.values(retencoes ?? {}).reduce<number>(
    (s, v) => s + toNumber(v as number),
    0,
  );
  const liquido = Math.max(0, valor - federais - issRetido);

  const linha = (rotulo: string, v: string, total = false) => (
    <>
      <Text
        fontSize="13px"
        color={total ? C.ink : C.muted}
        fontWeight={total ? 700 : 400}
        pt={total ? 2 : 0}
        borderTopWidth={total ? '1px' : 0}
        borderColor={C.line}
      >
        {rotulo}
      </Text>
      <Text
        fontSize={total ? '15px' : '13px'}
        fontWeight={total ? 700 : 500}
        textAlign="right"
        fontFamily={MONO}
        pt={total ? 2 : 0}
        borderTopWidth={total ? '1px' : 0}
        borderColor={C.line}
      >
        {v}
      </Text>
    </>
  );

  return (
    <Grid as="dl" templateColumns="1fr auto" rowGap={1.5} columnGap={3}>
      {linha('Valor do serviço', brl(valor))}
      {linha(
        `ISS (${String(trib) === '1' ? `${aliquota.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%` : 'isento'})${issRetido ? ' · retido' : ''}`,
        brl(iss),
      )}
      {linha('Retenções federais', federais ? `− ${brl(federais)}` : brl(0))}
      {issRetido ? linha('ISS retido', `− ${brl(issRetido)}`) : null}
      {linha('Valor líquido', brl(liquido), true)}
    </Grid>
  );
}

export function VerificacoesPanel({
  emitente,
  validacao,
  errosSefin,
  numeroDps,
  serie,
  bloqueado,
  reenvio,
  salvando,
  validando,
  enviando,
  onSalvar,
  onValidar,
  onEmitir,
  onUsarProximoLivre,
}: VerificacoesPanelProps) {
  const ocupado = salvando || validando || enviando;
  const notaExistente = validacao?.nota_existente;
  const numero = numeroDps ?? emitente?.proximo_n_dps;
  const problemas = validacao ? validacao.verificacoes.filter(v => !v.ok).length : 0;

  return (
    <Flex direction="column" gap="18px" position={{ xl: 'sticky' }} top={{ xl: '64px' }}>
      <Card p={5}>
        <CardHead title="Verificações" description="Rodam antes de qualquer envio à Sefin" />
        <CheckList items={verificacoes(emitente, validacao, validando, numero)} />
        {notaExistente && (
          <Box mt={3}>
            <Banner
              tone="danger"
              compact
              action={
                validacao?.proximo_livre ? (
                  <NButton
                    size="sm"
                    variant="secondary"
                    onClick={() => onUsarProximoLivre(validacao.proximo_livre as number)}
                  >
                    Usar o nº {validacao.proximo_livre}
                  </NButton>
                ) : undefined
              }
            >
              <b>
                O nº {notaExistente.n_dps} da série {notaExistente.serie} já foi emitido
              </b>{' '}
              ({notaExistente.origem === 'sefin' ? 'na Sefin' : 'nas nossas tabelas'}, em{' '}
              {dataBR(notaExistente.data)}
              {notaExistente.tomador_nome ? ` para ${notaExistente.tomador_nome}` : ''}). A emissão
              foi bloqueada.
            </Banner>
          </Box>
        )}
      </Card>

      <Card p={5}>
        <CardHead
          title="Resumo"
          right={
            serie && numero ? (
              <Text fontSize="12px" color={C.subtle}>
                DPS {serie} · nº {numero}
              </Text>
            ) : undefined
          }
        />
        <Resumo />
      </Card>

      <Flex direction="column" gap={2}>
        {!!errosSefin?.length && !validacao && (
          <Banner tone="danger" compact>
            Corrija {errosSefin.length > 1 ? 'os campos apontados' : 'o campo apontado'} pela Sefin
            e valide de novo.
          </Banner>
        )}
        {validacao && !validacao.ok && (
          <Banner tone="danger" icon={Warning} compact>
            {problemas} verificação(ões) com problema. O motivo está em cada linha acima.
          </Banner>
        )}
        {validacao?.ok && (
          <Banner tone="success" icon={CheckCircle} compact>
            Tudo certo. A DPS está pronta para assinar e enviar.
          </Banner>
        )}
        <NButton
          variant="secondary"
          icon={CheckCircle}
          isLoading={validando}
          loadingText="Validando…"
          isDisabled={bloqueado || ocupado}
          onClick={onValidar}
        >
          Validar
        </NButton>
        <NButton
          icon={PaperPlaneTilt}
          isLoading={enviando}
          loadingText="Enviando…"
          isDisabled={bloqueado || ocupado || !validacao?.ok}
          onClick={onEmitir}
        >
          {reenvio ? 'Reenviar NFS-e' : 'Emitir NFS-e'}
        </NButton>
        <NButton
          variant="ghost"
          isLoading={salvando}
          isDisabled={bloqueado || ocupado}
          onClick={onSalvar}
        >
          Salvar rascunho
        </NButton>
        <Text fontSize="12px" color={C.subtle} textAlign="center" mt={0.5}>
          Emitir só libera depois de validar. Dois cliques não geram duas notas.
        </Text>
      </Flex>
    </Flex>
  );
}
